/**
 * A festa: o instante em que o ponto chega.
 *
 * O `xp_progress` já andava — quem soma é `server/src/xp.js`, na porta única por
 * onde os quatro apps escrevem — mas ninguém avisava ninguém. A pessoa guardava
 * uma receita numa tela, voltava ao painel e a barra estava um pouco mais cheia
 * do que da última vez. Um sistema de pontos que não diz "você ganhou" é uma
 * planilha: a conta existe, a recompensa não.
 *
 * Por que a diferença é calculada aqui e não vem do servidor: o `premiar`
 * devolve o evento da escrita, e não o quanto somou nem se cruzou um nível.
 * Fazê-lo devolver isso obrigaria cada rota a carregar o troco até a resposta,
 * e a resposta do `PUT /dados` é lida por quem gravou — que muitas vezes não é
 * a tela onde a barra está. Então o cliente guarda o último total que já mostrou
 * e compara com o que chegou. A conta é de subtração e nunca discorda do
 * servidor: ela não inventa total nenhum, só olha dois.
 *
 * O que isso dá de graça: ponto ganho com o painel fechado — ou no aparelho do
 * outro — também vira festa na próxima vez que a barra aparecer. É o "enquanto
 * você não olhava" que um marcador na mão do servidor não teria como dar sem
 * guardar um marcador por aparelho.
 *
 * O que não veio, e de propósito: **som**. O Flutter chama
 * `AudioService().playSfx('level_up.mp3')` em `xp_service.dart:53`, e os três
 * arquivos de `assets/audio/` (`level_up`, `pop`, `sparkle`) são byte a byte o
 * mesmo arquivo de 417 bytes — um cabeçalho MP3 seguido de zeros, que o
 * `ffprobe` recusa a decodificar. O upstream sempre tocou silêncio. Um som novo
 * aqui seria um som que ninguém pediu e que não há onde desligar: a casa não
 * tem ajuste de áudio para desligá-lo. Se um dia houver, que seja pela Web Audio
 * API e não por um `<audio>` — um elemento de mídia tocando dispararia o ouvinte
 * de `play` de `nucleo/tocador.tsx` e calaria a música da pessoa para fazer
 * "ping".
 */

import { useEffect, useRef, useState } from 'react';

import { ligacao } from '../nucleo/ligacao';
import { PONTOS_POR_NIVEL, type Progresso } from './progresso';

/**
 * Onde mora o último total que cada pessoa já viu.
 *
 * Uma chave só, com um mapa de slug para total dentro, e não uma chave por
 * pessoa: no celular o `Guardado` é um espelho em memória semeado de uma lista
 * fixa de chaves (`packages/mobile/src/nucleo/api.ts`), e um nome que muda com
 * quem entrou não caberia nessa lista.
 */
export const CHAVE_XP_VISTO = 'ns_xp_visto';

/** Quanto tempo o aviso fica na tela. */
export const DURACAO_DA_FESTA = 4000;

/** O que aconteceu desde a última vez que esta pessoa olhou para a barra. */
export interface Festa {
  /** Quantos pontos chegaram. Zero quando não há o que comemorar. */
  ganhou: number;
  /** Se esses pontos cruzaram um nível. */
  subiu: boolean;
}

/** Referência estável: devolvê-la sempre faz o `useState` desistir do render. */
const NADA: Festa = { ganhou: 0, subiu: false };

/**
 * O nível de um total, pela mesma conta do servidor (`server/src/xp.js:164`) e
 * do Flutter. Não se lê o campo `level` do documento porque só se tem um deles:
 * do total antigo sobrou o número, não a ficha inteira.
 */
function nivelDe(total: number): number {
  return Math.floor(total / PONTOS_POR_NIVEL) + 1;
}

/**
 * A decisão inteira, sem React e sem disco, para caber num teste.
 *
 * `antes` nulo é quem nunca foi anotado: a primeira visita depois desta versão
 * chegar ao aparelho. Comemorar aí seria anunciar "+3400 XP" para quem não
 * ganhou nada agora — o total de um ano inteiro de casa virando festa de uma
 * atualização. Anota calado e comemora a partir do próximo ponto.
 */
export function festaEntre(antes: number | null, agora: number): Festa {
  if (antes === null || agora <= antes) return NADA;
  return { ganhou: agora - antes, subiu: nivelDe(agora) > nivelDe(antes) };
}

/** O mapa inteiro, ou vazio se ninguém escreveu ainda — ou escreveu torto. */
function vistos(): Record<string, unknown> {
  const cru = ligacao().guardado.ler(CHAVE_XP_VISTO);
  if (!cru) return {};
  try {
    const v: unknown = JSON.parse(cru);
    return v !== null && typeof v === 'object' && !Array.isArray(v)
      ? (v as Record<string, unknown>)
      : {};
  } catch {
    // Disco torto é a mesma coisa que disco vazio: a pessoa perde uma festa, e
    // a próxima já sai do lugar certo.
    return {};
  }
}

function anotar(quem: string, total: number): void {
  ligacao().guardado.gravar(CHAVE_XP_VISTO, JSON.stringify({ ...vistos(), [quem]: total }));
}

/**
 * O que mostrar por cima da barra agora, e por quatro segundos.
 *
 * Recebe `null` enquanto não há quem ou o documento não chegou — a barra chama
 * este gancho antes do `return null` dela, porque gancho não pode nascer depois
 * de um `return`.
 *
 * O `ultima` não é otimização: em desenvolvimento o React roda todo efeito duas
 * vezes, e a segunda passada leria o marcador que a primeira acabou de gravar,
 * acharia diferença zero e apagaria a festa antes de alguém a ver. O par
 * "pessoa mais total" é o que já foi decidido; vê-lo de novo não decide nada.
 */
export function usarFesta(p: Progresso | null): Festa {
  const [festa, setFesta] = useState<Festa>(NADA);
  const ultima = useRef('');
  const quem = p?.quem ?? '';
  const total = p?.total ?? 0;

  useEffect(() => {
    if (!quem) {
      setFesta(NADA);
      return;
    }
    const marca = `${quem}:${total}`;
    if (ultima.current === marca) return;
    ultima.current = marca;

    const antes = vistos()[quem];
    anotar(quem, total);
    setFesta(festaEntre(typeof antes === 'number' ? antes : null, total));
  }, [quem, total]);

  /* Separado do de cima porque a segunda passada do desenvolvimento desmonta o
     primeiro efeito e não o refaz: um relógio armado lá dentro seria cancelado
     na limpeza e nunca mais rearmado, e a festa ficaria na tela para sempre. */
  useEffect(() => {
    if (festa === NADA) return;
    const relogio = setTimeout(() => setFesta(NADA), DURACAO_DA_FESTA);
    return () => clearTimeout(relogio);
  }, [festa]);

  return festa;
}
